/**
 * OfflinePanel — user-facing offline control + sync tracking, backed by the
 * encrypted desktop offline service (lib/offline). Shows a manual "Work Offline"
 * toggle, connection/sync status, pending-operation list, conflicts, and a manual
 * "Sync now". Render as a modal (sidebar) or embedded (Settings).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useOfflineState, useSync, useConflicts, useOfflineControls } from '@/lib/offline/hooks';
import { SyncConflictModal } from './SyncConflictModal';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { RefreshCw, WifiOff, Wifi, X } from 'lucide-react';

interface QueueRow {
  id: string;
  type?: string;
  operation_type?: string;
  entityType?: string;
  entity_type?: string;
  status?: string;
  retryCount?: number;
  retry_count?: number;
  errorMessage?: string;
  error_message?: string;
}

function fmtTime(ts: number | null): string {
  if (!ts) return 'Never';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return 'Unknown';
  }
}

export function OfflinePanelContent() {
  const state = useOfflineState();
  const { syncNow, isSyncing, pendingCount, lastSyncAt } = useSync();
  const { hasConflicts, conflicts } = useConflicts();
  const { isDesktop, setForcedOffline, getAllOperations } = useOfflineControls();

  const [ops, setOps] = useState<QueueRow[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [busyToggle, setBusyToggle] = useState(false);

  const refreshOps = useCallback(async () => {
    const all = (await getAllOperations()) as unknown as QueueRow[];
    setOps(all);
  }, [getAllOperations]);

  useEffect(() => {
    void refreshOps();
  }, [refreshOps, state.pendingOperations, isSyncing]);

  const onToggle = async (value: boolean) => {
    setBusyToggle(true);
    try {
      await setForcedOffline(value);
    } finally {
      setBusyToggle(false);
    }
  };

  const statusLabel = state.forcedOffline
    ? 'Working Offline (manual)'
    : state.isOnline
    ? 'Online'
    : 'Offline';
  const statusColor = state.forcedOffline
    ? 'bg-amber-100 text-amber-700'
    : state.isOnline
    ? 'bg-green-100 text-green-700'
    : 'bg-gray-200 text-gray-700';

  return (
    <div className="space-y-4">
      {/* Status + manual toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {state.isOnline ? (
            <Wifi className="h-5 w-5 text-green-600" />
          ) : (
            <WifiOff className="h-5 w-5 text-amber-600" />
          )}
          <span className="font-semibold text-gray-900 dark:text-white">{statusLabel}</span>
          <Badge variant="secondary" className={statusColor}>
            {state.syncStatus}
          </Badge>
        </div>
      </div>

      {isDesktop && (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Work Offline</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Queue changes locally and stop syncing until you turn this off.
            </p>
          </div>
          <Switch
            checked={state.forcedOffline}
            disabled={busyToggle}
            onCheckedChange={(v) => void onToggle(Boolean(v))}
          />
        </div>
      )}

      {/* Sync summary */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">Pending changes</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">{pendingCount}</p>
        </div>
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">Last sync</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{fmtTime(lastSyncAt)}</p>
        </div>
      </div>

      {state.error && (
        <p className="rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {state.error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => void syncNow()} disabled={isSyncing || !state.isOnline}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing…' : 'Sync now'}
        </Button>
        {hasConflicts && (
          <Button size="sm" variant="outline" onClick={() => setShowConflicts(true)}>
            Resolve {conflicts.length} conflict{conflicts.length === 1 ? '' : 's'}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => void refreshOps()}>
          Refresh
        </Button>
      </div>

      {/* Per-operation queue */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Queue ({ops.length})
        </p>
        {ops.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing queued.</p>
        ) : (
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {ops.map((op) => {
              const type = op.type ?? op.operation_type ?? '?';
              const entity = op.entityType ?? op.entity_type ?? '?';
              const retries = op.retryCount ?? op.retry_count ?? 0;
              const err = op.errorMessage ?? op.error_message;
              return (
                <div
                  key={op.id}
                  className="flex items-center justify-between rounded border border-gray-100 px-2 py-1.5 text-xs dark:border-gray-700"
                >
                  <span className="truncate text-gray-700 dark:text-gray-300">
                    {String(type).toUpperCase()} · {entity}
                    {err ? <span className="text-red-600"> — {err}</span> : null}
                  </span>
                  <Badge
                    variant="secondary"
                    className={
                      op.status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : op.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }
                  >
                    {op.status}
                    {retries ? ` (${retries})` : ''}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SyncConflictModal open={showConflicts} onOpenChange={setShowConflicts} />
    </div>
  );
}

/** Modal wrapper for the sidebar status dot. */
export function OfflinePanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Offline &amp; Sync</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <OfflinePanelContent />
      </div>
    </div>
  );
}
